import { nil, NO, UITextField, UITextView, UIView, YES } from "uicore-ts"
import { RHTextArea } from "./RHTextArea"
import { RHButtons, RHDialogs } from "./Utils_slop/RHDesignTokens"


/**
 * One removable e-mail chip. Self-laying-out so its width hugs
 * the address; the owner wraps rows of these above the text area.
 */
class RHEmailRecipientView extends UIView {

    readonly email: string
    removeWasPressed?: () => void

    readonly emailLabel = new UITextView().configuredWithObject({
        textColor: RHDialogs.share.chipTextColor,
        isSingleLine: YES,
        style: { fontSize: RHDialogs.share.chipFontSize }
    }).addedAsSubviewToView(this)

    readonly removeButton = new UITextView().configuredWithObject({
        hoverText: "Eemalda adressaat",
        text: RHButtons.deleteIconHTML,
        textColor: RHDialogs.share.chipRemoveTextColor,
        textAlignment: UITextView.textAlignment.center,
        style: { cursor: "pointer", fontSize: "16px", lineHeight: "1" }
    }).addedAsSubviewToView(this)


    constructor(email: string) {
        super()
        this.email = email
        this.emailLabel.text = email
        ;(this as RHEmailRecipientView).configureWithObject({
            backgroundColor: RHDialogs.share.chipBackgroundColor,
            style: {
                borderRadius: RHDialogs.share.chipBorderRadius,
                border: `1px solid ${RHDialogs.share.chipBorderColor.stringValue}`
            }
        })
        this.removeButton.controlEventTargetAccumulator.PointerUpInside = () => {
            this.removeWasPressed?.()
        }
    }


    override intrinsicContentWidth(constrainingHeight?: number): number {
        const tokens = RHDialogs.share
        return this.emailLabel.intrinsicContentWidth(constrainingHeight)
            + tokens.chipHorizontalPadding * 2
            + tokens.chipLabelToRemoveGap
            + tokens.chipRemoveButtonSize
    }


    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHDialogs.share
        this.contentBoundsWithInsets(
            tokens.chipHorizontalPadding, tokens.chipHorizontalPadding, 0, 0
        ).distributeViewsAlongWidth(
            [this.emailLabel, this.removeButton],
            undefined,
            tokens.chipLabelToRemoveGap,
            [nil, tokens.chipRemoveButtonSize]
        )
    }

}


/**
 * RHEmailRecipientsInputView — a Gmail-style recipient input. Typing addresses
 * separated by comma, semicolon, whitespace, or line breaks turns them into
 * removable chips; valid, de-duplicated addresses are exposed via `recipients()`.
 * This collects the recipients only — the actual sending is wired separately.
 */
export class RHEmailRecipientsInputView extends UIView {

    private static readonly _recipientSeparatorPattern = /[;,\s]+/

    _recipients: string[] = []
    _chipViews: RHEmailRecipientView[] = []

    recipientsDidChange?: () => void

    readonly textArea = new RHTextArea().addedAsSubviewToView(this)


    constructor(elementID?: string) {
        super(elementID)
        const tokens = RHDialogs.share

        ;(this as RHEmailRecipientsInputView).configureWithObject({
            backgroundColor: tokens.inputBackgroundColor,
            style: {
                borderRadius: tokens.inputBorderRadius,
                border: `1px solid ${tokens.inputBorderColor.stringValue}`
            }
        })

        this.textArea.placeholderText = tokens.inputPlaceholder
        // The container carries the border; the editor itself is borderless.
        this.textArea.configureWithObject({
            style: {
                border: "none",
                boxShadow: "none",
                backgroundColor: "transparent"
            }
        })
        this.textArea.textElementView.configureWithObject({
            style: {
                padding: "0",
                backgroundColor: "transparent"
            }
        })

        this.controlEventTargetAccumulator.PointerUpInside = () => {
            this.textArea.focus()
        }

        // Enter commits the whole current input.
        this.textArea.textElementView.controlEventTargetAccumulator.EnterDown = (_sender: UIView, event: Event) => {
            event.preventDefault()
            this.commitPendingText()
        }
        // A typed separator commits everything before it; the remainder keeps typing.
        this.textArea.addTargetForControlEvent(UITextField.controlEvent.TextChange, () => {
            const text = this.textArea.text
            if (!this._textContainsSeparator(text)) {
                this.recipientsDidChange?.()
                return
            }
            const segments = this._emailSegmentsFromText(text)
            const remainder = segments.pop() ?? ""
            for (const segment of segments) {
                this._tryAddEmail(segment)
            }
            this.textArea.text = remainder.trim()
            this.recipientsDidChange?.()
        })
        // Clicking away commits finished addresses.
        this.textArea.addTargetForControlEvent(UIView.controlEvent.Blur, () => {
            this.commitPendingText()
        })
    }


    recipients(): string[] {
        return [...this._recipients]
    }


    clear() {
        this.textArea.text = ""
        this._recipients = []
        this._rebuildChips()
        this.recipientsDidChange?.()
    }


    commitPendingText(): boolean {
        const segments = this._emailSegmentsFromText(this.textArea.text)
            .map(segment => segment.trim())
            .filter(segment => segment.length > 0)
        if (segments.length === 0) {
            return YES
        }

        for (const segment of segments) {
            if (!this._isValidEmail(segment)) {
                return NO
            }
        }

        for (const segment of segments) {
            this._tryAddEmail(segment)
        }
        this.textArea.text = ""
        this.recipientsDidChange?.()
        return YES
    }


    hasValidPendingRecipients(): boolean {
        const segments = this._emailSegmentsFromText(this.textArea.text)
            .map(segment => segment.trim())
            .filter(segment => segment.length > 0)
        if (segments.length === 0) {
            return NO
        }
        return segments.every(segment => this._isValidEmail(segment))
    }


    _isValidEmail(value: string): boolean {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
    }


    // Returns YES when the input was consumed (added or already present).
    _tryAddEmail(email: string): boolean {
        const normalizedEmail = email.trim().toLowerCase()
        if (normalizedEmail.length === 0 || !this._isValidEmail(normalizedEmail)) {
            return NO
        }
        if (this._recipients.includes(normalizedEmail)) {
            return YES
        }
        this._recipients.push(normalizedEmail)
        this._rebuildChips()
        this.recipientsDidChange?.()
        return YES
    }


    _removeRecipient(email: string) {
        this._recipients = this._recipients.filter(existing => existing !== email)
        this._rebuildChips()
        this.recipientsDidChange?.()
    }


    private _textContainsSeparator(text: string): boolean {
        return RHEmailRecipientsInputView._recipientSeparatorPattern.test(text)
    }


    private _emailSegmentsFromText(text: string): string[] {
        return text.split(RHEmailRecipientsInputView._recipientSeparatorPattern)
    }


    _rebuildChips() {
        for (const chip of this._chipViews) {
            chip.removeFromSuperview()
        }
        this._chipViews = this._recipients.map(email => {
            const chip = new RHEmailRecipientView(email).addedAsSubviewToView(this)
            chip.removeWasPressed = () => this._removeRecipient(email)
            return chip
        })
        this.setNeedsLayout()
    }


    // ── Layout: wrap chips at the top, then let the text area fill the rest ─────

    override layoutSubviews() {
        super.layoutSubviews()
        const tokens = RHDialogs.share
        const bounds = this.contentBoundsWithInset(tokens.inputContainerInset)
        const rowHeight = tokens.inputRowHeight
        const gap = tokens.inputItemGap

        const rows = this._chipRowsForLayout(bounds.width, rowHeight, gap)

        if (rows.length === 0) {
            this.textArea.frame = bounds
            return
        }

        let rowFrame = bounds.rectangleWithHeight(rowHeight)
        rows.forEach((row, rowIndex) => {
            if (rowIndex > 0) {
                rowFrame = rowFrame.rectangleForNextRow(gap, rowHeight)
            }
            rowFrame.framesByDistributingViewsAsRow(row.items, gap, row.widths)
        })

        const textAreaHeight = Math.max(rowHeight, bounds.max.y - rowFrame.max.y - gap)
        rowFrame
            .rectangleForNextRow(gap, textAreaHeight)
            .assignedAsFrameOfView(this.textArea)
    }


    // A roomy minimum box; it grows taller once enough chips wrap past the minimum.
    override intrinsicContentHeight(constrainingWidth: number = 0): number {
        const tokens = RHDialogs.share
        const availableWidth = Math.max(0, constrainingWidth - tokens.inputContainerInset * 2)
        const rows = this._chipRowsForLayout(availableWidth, tokens.inputRowHeight, tokens.inputItemGap)
        let contentHeight = tokens.inputRowHeight
        if (rows.length > 0) {
            contentHeight = (rows.length + 1) * tokens.inputRowHeight + rows.length * tokens.inputItemGap
        }
        return Math.max(
            tokens.inputMinHeight,
            contentHeight + tokens.inputContainerInset * 2
        )
    }


    // Greedy wrap of the chips. The text area is laid out separately so it can
    // fill the whole remaining box instead of behaving like a short row item.
    _chipRowsForLayout(availableWidth: number, rowHeight: number, gap: number): { items: UIView[], widths: number[] }[] {
        const rows: { items: UIView[], widths: number[] }[] = []

        let items: UIView[] = []
        let widths: number[] = []
        let usedWidth = 0

        for (const chip of this._chipViews) {
            const chipWidth = chip.intrinsicContentWidth(rowHeight)
            let widthIfAppended = chipWidth
            if (items.length > 0) {
                widthIfAppended = usedWidth + gap + chipWidth
            }
            if (items.length > 0 && widthIfAppended > availableWidth) {
                rows.push({ items, widths })
                items = []
                widths = []
                usedWidth = 0
            }
            items.push(chip)
            widths.push(chipWidth)
            usedWidth = items.length === 1 ? chipWidth : usedWidth + gap + chipWidth
        }

        if (items.length > 0) {
            rows.push({ items, widths })
        }

        return rows
    }

}
